import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ShieldCheck, AlertTriangle, Loader2, Zap, Layers } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const fmtUsd = (v) => `$${(Number(v) || 0).toFixed(2)}`;

export const SpendControls = () => {
  const [data, setData] = useState(null);
  const [capInput, setCapInput] = useState('');
  const [dailyCapInput, setDailyCapInput] = useState('');
  const [bgRatioInput, setBgRatioInput] = useState('');       // percent 0-100
  const [backfillCapInput, setBackfillCapInput] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/tenant/billing/spend-controls');
      setData(res.data);
      setCapInput(res.data?.monthly_spend_cap_usd ? String(res.data.monthly_spend_cap_usd) : '');
      setDailyCapInput(res.data?.daily_spend_cap_usd ? String(res.data.daily_spend_cap_usd) : '');
      setBgRatioInput(res.data?.background_spend_ratio != null ? String(Math.round(res.data.background_spend_ratio * 100)) : '');
      setBackfillCapInput(res.data?.daily_backfill_score_cap != null ? String(res.data.daily_backfill_score_cap) : '');
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (clear = false) => {
    setSaving(true);
    try {
      const cap = clear ? null : parseFloat(capInput);
      if (!clear && (isNaN(cap) || cap <= 0)) { toast.error('Enter a valid monthly cap (USD)'); setSaving(false); return; }
      const res = await api.put('/api/tenant/billing/spend-controls', { monthly_spend_cap_usd: cap });
      toast.success(clear ? 'Spend cap removed' : `Monthly AI spend cap set to ${fmtUsd(res.data.monthly_spend_cap_usd)}`);
      if (clear) setCapInput('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update spend controls');
    } finally { setSaving(false); }
  };

  const saveDaily = async (clear = false) => {
    setSaving(true);
    try {
      const dcap = clear ? 0 : parseFloat(dailyCapInput);
      if (!clear && (isNaN(dcap) || dcap <= 0)) { toast.error('Enter a valid daily cap (USD)'); setSaving(false); return; }
      const res = await api.put('/api/tenant/billing/spend-controls', { daily_spend_cap_usd: dcap });
      toast.success(clear ? 'Daily cap removed' : `Daily AI spend cap set to ${fmtUsd(res.data.daily_spend_cap_usd)}`);
      if (clear) setDailyCapInput('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update daily cap');
    } finally { setSaving(false); }
  };

  const saveBgRatio = async () => {
    setSaving(true);
    try {
      const pctVal = parseFloat(bgRatioInput);
      if (isNaN(pctVal) || pctVal < 0 || pctVal > 100) { toast.error('Enter a value between 0 and 100'); setSaving(false); return; }
      const ratio = Math.round(pctVal) / 100;
      await api.put('/api/tenant/billing/spend-controls', { background_spend_ratio: ratio });
      toast.success(`Autonomous AI limited to ${Math.round(pctVal)}% of the daily cap`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update budget lane');
    } finally { setSaving(false); }
  };

  const saveBackfillCap = async () => {
    setSaving(true);
    try {
      const capVal = parseInt(backfillCapInput, 10);
      if (isNaN(capVal) || capVal < 0) { toast.error('Enter a valid number (0 disables)'); setSaving(false); return; }
      await api.put('/api/tenant/billing/spend-controls', { daily_backfill_score_cap: capVal });
      toast.success(capVal === 0 ? 'Background catch-up scoring disabled' : `Background catch-up limited to ${capVal} leads/day`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update backfill cap');
    } finally { setSaving(false); }
  };

  if (!data) return null;
  const cap = data.monthly_spend_cap_usd;
  const pct = data.usage_pct;
  const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500';
  const dailyCap = data.daily_spend_cap_usd;
  const dailySpend = data.daily_spend_usd || 0;
  const dailyPct = dailyCap ? Math.round((dailySpend / dailyCap) * 100) : 0;
  const bgSpend = data.daily_background_spend_usd || 0;
  const userSpend = Math.max(0, dailySpend - bgSpend);
  const pctOfCap = (v) => dailyCap ? Math.min((v / dailyCap) * 100, 100) : 0;

  return (
    <Card data-testid="spend-controls-card" className="border-emerald-500/25">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10"><ShieldCheck className="h-5 w-5 text-emerald-500" /></div>
          <div>
            <CardTitle>Spend Controls</CardTitle>
            <CardDescription>Cap your monthly AI spend and get alerted at 50% / 80% / 100%</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Low balance one-tap top-up */}
        {data.low_balance && (
          <Alert variant="destructive" data-testid="spend-low-balance-alert">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between gap-3">
              <span>Low balance ({fmtUsd(data.credit_balance_usd)}). Top up so cadences, calls & WhatsApp don't pause.</span>
              <Button size="sm" data-testid="spend-topup-btn"
                onClick={() => document.querySelector('[data-testid="credit-balance-card"]')?.scrollIntoView({ behavior: 'smooth' })}>
                <Zap className="h-3 w-3 mr-1" />Top up
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Cap setter */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">Monthly AI spend cap (USD)</label>
            <Input type="number" min="0" step="1" placeholder="No cap — unlimited"
              value={capInput} onChange={(e) => setCapInput(e.target.value)} data-testid="spend-cap-input" />
          </div>
          <Button onClick={() => save(false)} disabled={saving} data-testid="spend-cap-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set cap'}
          </Button>
          {cap ? <Button variant="ghost" onClick={() => save(true)} disabled={saving} data-testid="spend-cap-clear">Remove</Button> : null}
        </div>

        {/* Usage meter */}
        {cap ? (
          <div data-testid="spend-usage-meter">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">This cycle: <span className="font-semibold text-foreground">{fmtUsd(data.cycle_spend_usd)}</span> / {fmtUsd(cap)}</span>
              <div className="flex gap-1">
                {[50, 80, 100].map((t) => (
                  <Badge key={t} variant={data.alerts_crossed?.includes(t) ? (t === 100 ? 'destructive' : 'default') : 'outline'} className="text-[10px]">{t}%</Badge>
                ))}
              </div>
            </div>
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(pct || 0, 100)}%` }} />
            </div>
            {data.cap_reached && (
              <p className="text-xs text-red-400 mt-2" data-testid="spend-cap-reached-note">Cap reached — AI spend is paused until you raise the cap or the cycle resets.</p>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No cap set — AI spend is limited only by your credit balance.</p>
        )}

        {/* Daily $ budget governor */}
        <div className="pt-4 border-t border-border/40">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Daily AI spend cap (USD) — hard stop for all AI spend today</label>
              <Input type="number" min="0" step="1" placeholder="No daily cap"
                value={dailyCapInput} onChange={(e) => setDailyCapInput(e.target.value)} data-testid="daily-cap-input" />
            </div>
            <Button onClick={() => saveDaily(false)} disabled={saving} data-testid="daily-cap-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set'}
            </Button>
            {dailyCap ? <Button variant="ghost" onClick={() => saveDaily(true)} disabled={saving} data-testid="daily-cap-clear">Remove</Button> : null}
          </div>
          {dailyCap ? (
            <div className="mt-2" data-testid="daily-usage-meter">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-muted-foreground">Today: <span className="font-semibold text-foreground">{fmtUsd(dailySpend)}</span> / {fmtUsd(dailyCap)}</span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div className={`h-full ${dailyPct >= 100 ? 'bg-red-500' : dailyPct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'} transition-all`} style={{ width: `${Math.min(dailyPct, 100)}%` }} />
              </div>
              {data.daily_cap_reached && (
                <p className="text-xs text-red-400 mt-2" data-testid="daily-cap-reached-note">Daily cap reached — all AI spend is paused until tomorrow (UTC) or you raise the cap.</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">No daily cap — AI spend is bounded only by the monthly cap & balance.</p>
          )}
        </div>

        {/* Phase 1 · Budget Lanes — reserve daily-cap headroom for user actions */}
        <div className="pt-4 border-t border-border/40" data-testid="budget-lanes-section">
          <div className="flex items-center gap-2 mb-1">
            <Layers className="h-4 w-4 text-emerald-500" />
            <span className="text-sm font-semibold">Budget Lanes</span>
            <Badge variant="outline" className="text-[10px]">Advanced</Badge>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Reserve part of your daily cap for your own actions (Brain Chat, campaigns) so background AI can never spend it all.
          </p>

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Max % of daily cap for autonomous AI</label>
              <Input type="number" min="0" max="100" step="5" placeholder="50"
                value={bgRatioInput} onChange={(e) => setBgRatioInput(e.target.value)} data-testid="bg-ratio-input" />
            </div>
            <Button onClick={saveBgRatio} disabled={saving} data-testid="bg-ratio-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set'}
            </Button>
          </div>
          {dailyCap ? (
            <div className="mt-2" data-testid="lane-usage-bar">
              <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                <span>Today vs daily cap ({fmtUsd(dailyCap)})</span>
                <span className="font-medium text-foreground">{fmtUsd(dailySpend)} used</span>
              </div>
              <div className="relative h-3 w-full rounded-full bg-muted overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-amber-500/80 transition-all"
                  style={{ width: `${pctOfCap(bgSpend)}%` }} data-testid="lane-bar-autonomous" />
                <div className="absolute inset-y-0 bg-emerald-500/80 transition-all"
                  style={{ left: `${pctOfCap(bgSpend)}%`, width: `${Math.max(0, pctOfCap(bgSpend + userSpend) - pctOfCap(bgSpend))}%` }}
                  data-testid="lane-bar-user" />
                <div className="absolute inset-y-0 w-0.5 bg-foreground/70"
                  style={{ left: `${Math.min((data.background_spend_ratio || 0) * 100, 100)}%` }}
                  title="Autonomous lane limit" data-testid="lane-bar-limit" />
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-amber-500/80" />Autonomous {fmtUsd(bgSpend)}</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-emerald-500/80" />You {fmtUsd(userSpend)}</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-0.5 bg-foreground/70" />Autonomous limit {Math.round((data.background_spend_ratio || 0) * 100)}% ({fmtUsd(data.daily_background_cap_usd)})</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="bg-lane-hint">
                Background AI can use up to <span className="font-medium text-foreground">{fmtUsd(data.daily_background_cap_usd)}</span>/day;
                the remaining <span className="font-medium text-foreground">{fmtUsd(dailyCap - (data.daily_background_cap_usd || 0))}</span> is always reserved for you.
              </p>
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground mt-1">Set a daily cap above to activate lane splitting.</p>
          )}

          <div className="flex items-end gap-2 mt-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Daily background catch-up scoring limit (leads/day, 0 = off)</label>
              <Input type="number" min="0" step="1" placeholder="200"
                value={backfillCapInput} onChange={(e) => setBackfillCapInput(e.target.value)} data-testid="backfill-cap-input" />
            </div>
            <Button onClick={saveBackfillCap} disabled={saving} data-testid="backfill-cap-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set'}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1" data-testid="backfill-cap-hint">
            Caps how many unscored leads the AI auto-scores per day. Currently{' '}
            <span className="font-medium text-foreground">{data.daily_backfill_score_cap}</span>
            {data.daily_backfill_score_cap === 0 ? ' (disabled)' : ' leads/day'}.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SpendControls;
